import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Layout from '../../components/ui/Layout';
import Breadcrumb from '../../components/ui/Breadcrumb';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import OrderStatusCard from './components/OrderStatusCard';
import OrderFilters from './components/OrderFilters';
import LiveOrderFeed from './components/LiveOrderFeed';
import OrderStatusChart from './components/OrderStatusChart';
import RefundRequestsPanel from './components/RefundRequestsPanel';
import OrderAnalyticsTable from './components/OrderAnalyticsTable';

const OrderIntelligenceCenter = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: 'all',
    timeRange: 'today'
  });

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleSearch = (searchTerm) => {
    // console.log('Searching for:', searchTerm);
  };

  const handleRefresh = async () => {
    setIsLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsLoading(false);
  };

  const handleExport = async () => {
    // Simulate export
    await new Promise(resolve => setTimeout(resolve, 2000));
    console.log('Export completed');
  };

  const quickActions = [
    {
      id: 'bulk-update',
      label: 'Bulk Update',
      icon: 'Edit',
      onClick: () => console.log('Bulk update'),
      variant: 'outline'
    },
    {
      id: 'notifications',
      label: 'Send Notifications',
      icon: 'Bell',
      onClick: () => console.log('Send notifications'),
      variant: 'outline'
    }
  ];

  if (isLoading) {
    return (
      <>
        <Helmet>
          <title>Order Intelligence Center - Qkly Admin Panel</title>
          <meta name="description" content="Real-time order monitoring and transaction analytics for marketplace administrators" />
        </Helmet>
        <Layout>
          <div className="px-6 py-8">
            <div className="animate-pulse space-y-6">
              <div className="h-8 bg-muted rounded w-1/4"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[...Array(4)]?.map((_, i) => (
                  <div key={i} className="h-32 bg-muted rounded-lg"></div>
                ))}
              </div>
              <div className="h-96 bg-muted rounded-lg"></div>
            </div>
          </div>
        </Layout>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Order Intelligence Center - Qkly Admin Panel</title>
        <meta name="description" content="Real-time order monitoring and transaction analytics for marketplace administrators" />
      </Helmet>
      <Layout>
        <div className="px-6 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <Breadcrumb className="mb-4" />
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-semibold text-foreground mb-2">Order Intelligence Center</h1>
                <p className="text-muted-foreground">
                  Real-time transaction monitoring and lifecycle tracking for marketplace operations
                </p>
              </div>
              <QuickActionToolbar
                actions={quickActions}
                onRefresh={handleRefresh}
                onExport={handleExport}
                onFilter={handleFilterChange}
                className="flex-shrink-0"
              />
            </div>
          </div>

          {/* Filters */}
          <OrderFilters 
            onFilterChange={handleFilterChange}
            onSearch={handleSearch}
          />

          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <OrderStatusCard
              title="Total Orders Today"
              value="1,839"
              change="+12.5%"
              changeType="positive"
              icon="ShoppingCart"
              color="primary"
            />
            <OrderStatusCard
              title="Completion Rate"
              value="94.2%"
              change="+2.1%"
              changeType="positive"
              icon="CheckCircle"
              color="success"
            />
            <OrderStatusCard
              title="Avg Fulfillment Time"
              value="2h 15m"
              change="-8m"
              changeType="positive"
              icon="Clock"
              color="warning"
            />
            <OrderStatusCard
              title="Dispute Rate"
              value="1.3%"
              change="+0.2%"
              changeType="negative"
              icon="AlertTriangle"
              color="error"
            />
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-8">
            {/* Live Order Feed */}
            <div className="lg:col-span-8">
              <LiveOrderFeed />
            </div>

            {/* Right Panel */}
            <div className="lg:col-span-4 space-y-6">
              <OrderStatusChart />
              <RefundRequestsPanel />
            </div>
          </div>

          {/* Order Analytics Table */}
          <OrderAnalyticsTable />
        </div>
      </Layout>
    </>
  );
};

export default OrderIntelligenceCenter;